import { CommonModule } from '@angular/common';
import { Component, OnDestroy, computed, effect, inject, untracked } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { PermissionContextService } from '@core/auth/permission-context.service';
import { POS_PERMISSIONS } from '@core/auth/pos-permissions.config';
import { MaterialModule } from 'src/app/material.module';
import { LocationContextService } from '@core/tenant/location-context.service';
import { PosOperationalContextService } from '../../../services/pos-operational-context.service';
import { PosStaffPinDialogMode } from '../../dialogs/pos-staff-pin.dialog';
import { PosStaffPinOpenerService } from '../../dialogs/pos-staff-pin.opener';
import { PosShiftPanelOpenerService } from '../../dialogs/pos-shift-panel.opener';

@Component({
  selector: 'app-pos-header-ops',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslateModule],
  templateUrl: './pos-header-ops.component.html',
  styleUrl: './pos-header-ops.component.scss',
})
export class PosHeaderOpsComponent implements OnDestroy {
  private readonly ops = inject(PosOperationalContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly permissions = inject(PermissionContextService);
  private readonly shiftPanels = inject(PosShiftPanelOpenerService);
  private readonly pinOpener = inject(PosStaffPinOpenerService);
  private pinOpen = false;

  readonly staff = this.ops.staff;
  readonly tipShiftSetupIncomplete = this.ops.tipShiftSetupIncomplete;
  readonly tipShiftActive = computed(
    () => this.ops.tipShiftTrackingEnabled() && this.ops.tipShiftOpen()
  );
  readonly tipShiftBlocked = computed(
    () => this.ops.tipShiftTrackingEnabled() && !this.ops.canCharge()
  );
  readonly canMutateTipShift = computed(() => {
    this.permissions.permissions();
    return this.permissions.can(
      POS_PERMISSIONS.tipShiftsCreate.resource,
      POS_PERMISSIONS.tipShiftsCreate.action
    );
  });
  /**
   * Chip de propinas (estado): visible si hay tracking/setup y no hay turno de caja
   * (con caja el tip va embebido). Acción de abrir panel solo con tip_shifts.create.
   */
  readonly showTipShiftChip = computed(() => {
    if (this.shiftRequired()) {
      return false;
    }
    return this.ops.tipShiftTrackingEnabled() || this.ops.tipShiftSetupIncomplete();
  });
  readonly canOpenTipShiftPanel = computed(() => this.canMutateTipShift());
  readonly tipShiftButtonLabelKey = computed(() => {
    if (this.tipShiftSetupIncomplete()) {
      return this.canOpenTipShiftPanel()
        ? 'pos.tipShift.setupShort'
        : 'pos.tipShift.statusSetupShort';
    }
    if (this.tipShiftActive()) {
      return this.canOpenTipShiftPanel()
        ? 'pos.tipShift.activeShort'
        : 'pos.tipShift.statusActiveShort';
    }
    if (this.tipShiftBlocked()) {
      return this.canOpenTipShiftPanel()
        ? 'pos.tipShift.startShort'
        : 'pos.tipShift.statusBlockedShort';
    }
    return 'pos.tipShift.short';
  });
  readonly tipShiftButtonCompactKey = computed(() => {
    if (this.tipShiftActive()) {
      return 'pos.tipShift.activeCompact';
    }
    if (this.tipShiftBlocked()) {
      return this.canOpenTipShiftPanel()
        ? 'pos.tipShift.startShort'
        : 'pos.tipShift.statusBlockedCompact';
    }
    if (this.tipShiftSetupIncomplete()) {
      return this.canOpenTipShiftPanel()
        ? 'pos.tipShift.setupShort'
        : 'pos.tipShift.statusSetupCompact';
    }
    return this.tipShiftButtonLabelKey();
  });
  readonly tipShiftButtonIcon = computed(() => {
    if (this.tipShiftSetupIncomplete()) {
      return 'tune';
    }
    if (this.tipShiftActive()) {
      return 'check_circle';
    }
    if (this.tipShiftBlocked()) {
      return 'schedule';
    }
    return 'schedule';
  });

  readonly shiftRequired = this.ops.shiftRequired;
  readonly canCashShiftOpen = computed(() => {
    this.permissions.permissions();
    return this.permissions.can(
      POS_PERMISSIONS.cashShiftsCreate.resource,
      POS_PERMISSIONS.cashShiftsCreate.action
    );
  });
  /** Estado del turno de caja: siempre visible si la sede lo exige. */
  readonly showCashShiftChip = computed(() => this.shiftRequired());
  /**
   * Turno abierto: todos abren el mismo sheet (mesero solo ve; update edita/cierra).
   * Turno cerrado: solo quien puede abrir (create).
   */
  readonly canOpenCashShiftPanel = computed(() => {
    if (!this.shiftRequired()) {
      return false;
    }
    if (this.cashShiftOpen()) {
      return true;
    }
    return this.canCashShiftOpen();
  });
  readonly cashShiftOpen = computed(() => this.ops.shiftRequired() && this.ops.shiftOpen());
  readonly cashShiftBlocked = computed(() => this.ops.shiftRequired() && !this.ops.shiftOpen());
  /** Abrir / Turno (mismo copy e icono para admin y mesero). */
  readonly cashShiftButtonLabelKey = computed(() => {
    if (!this.canOpenCashShiftPanel()) {
      return this.cashShiftOpen()
        ? 'pos.cashShift.statusOpenShort'
        : 'pos.cashShift.statusClosedShort';
    }
    if (!this.cashShiftOpen()) {
      return 'pos.cashShift.openShort';
    }
    return 'pos.cashShift.hubShort';
  });
  /** Texto corto para tablet/móvil (el full se oculta bajo 1024px). */
  readonly cashShiftButtonCompactKey = computed(() => {
    if (!this.canOpenCashShiftPanel()) {
      return this.cashShiftOpen()
        ? 'pos.cashShift.statusOpenCompact'
        : 'pos.cashShift.statusClosedCompact';
    }
    if (!this.cashShiftOpen()) {
      return 'pos.cashShift.openCompact';
    }
    return 'pos.cashShift.hubCompact';
  });
  readonly cashShiftButtonIcon = computed(() => {
    if (!this.canOpenCashShiftPanel()) {
      return this.cashShiftOpen() ? 'check_circle' : 'schedule';
    }
    if (!this.cashShiftOpen()) {
      return 'schedule';
    }
    return 'point_of_sale';
  });

  constructor() {
    effect(() => {
      const locationId = this.locationContext.activeLocationId();
      untracked(() => {
        if (locationId == null) {
          return;
        }
        this.ops.refresh().subscribe({ error: () => undefined });
      });
    });

    effect(() => {
      const needsLock = this.ops.lockRequired() && !this.ops.staff();
      if (!needsLock) {
        return;
      }
      untracked(() => this.openStaffPin('locked'));
    });
  }

  ngOnDestroy(): void {
    this.pinOpen = false;
  }

  openTipShift(): void {
    if (!this.canOpenTipShiftPanel()) {
      return;
    }
    this.shiftPanels.openTipShift().subscribe((changed) => {
      if (changed) {
        this.ops.refresh().subscribe({ error: () => undefined });
      }
    });
  }

  openCashShift(): void {
    if (!this.canOpenCashShiftPanel()) {
      return;
    }
    const intent = this.cashShiftOpen() ? 'close' : 'open';
    this.shiftPanels.openCashShift(intent).subscribe((changed) => {
      if (changed) {
        this.ops.refresh().subscribe({ error: () => undefined });
      }
    });
  }

  openStaffPin(mode: PosStaffPinDialogMode = 'identify'): void {
    if (this.pinOpen) {
      return;
    }
    this.pinOpen = true;
    this.pinOpener.open(mode).subscribe({
      next: () => {
        this.pinOpen = false;
        if (this.ops.lockRequired() && !this.ops.staff()) {
          queueMicrotask(() => this.openStaffPin('locked'));
        }
      },
      error: () => {
        this.pinOpen = false;
      },
    });
  }

  onOperatorClick(): void {
    this.openStaffPin('identify');
  }

  clearOperator(): void {
    this.ops.clearStaff();
  }
}
